import React, { useEffect, useMemo, useState } from "react";
import HRCampusSelector from "../../../components/HRCampusSelector";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../services/firebase";
import { getCurrentUser } from "../../../auth";

function lsKey(campus) {
  return `employees_${String(campus || "gen").toLowerCase()}`;
}
function loadLocalEmployees(campus) {
  try {
    const raw = localStorage.getItem(lsKey(campus));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function exportCSV(filename, rows) {
  const header = ["Name","Position","Department","Salary","Email","Phone","Campus"];
  const body = rows.map(r => [
    r.name || "",
    r.position || "",
    r.department || "",
    r.salary || "",
    r.email || "",
    r.phone || "",
    r.campus || ""
  ]);
  const csv = [header, ...body].map(r => r.map(x => `"${String(x).replace(/"/g,'""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function normalizeCampusLabel(x) {
  const s = String(x || "").toLowerCase();
  if (s.includes("gapan")) return "gapan";
  if (s.includes("bulacan")) return "bulacan";
  return s;
}

export default function EmployeeDirectory() {
  const [campus, setCampus] = useState(() => localStorage.getItem("hr_reports_campus") || "bulacan");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const user = getCurrentUser();
  const roleStr = String(user?.role || "").toLowerCase();
  const rolesArr = Array.isArray(user?.roles) ? user.roles.map(r => String(r).toLowerCase()) : [];
  const hasRole = (r) => roleStr === r || rolesArr.includes(r);
  const isExec = hasRole("admin") || hasRole("superadmin") || hasRole("vicepresident") || hasRole("vp") || hasRole("board") || hasRole("bot") || String(user?.campus || "").toLowerCase() === "all";
  useEffect(() => {
    try { localStorage.setItem("hr_reports_campus", campus); } catch (err) { void 0; }
  }, [campus]);
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, "employees"));
        let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        if (!isExec || campus) {
          const sc = normalizeCampusLabel(campus);
          list = list.filter(emp => normalizeCampusLabel(emp.campus) === sc);
        }
        setRows(list);
      } catch {
        let local = loadLocalEmployees(campus);
        if (!isExec || campus) {
          const sc = normalizeCampusLabel(campus);
          local = local.filter(emp => normalizeCampusLabel(emp.campus) === sc);
        }
        setRows(local);
      }
    })();
  }, [campus, isExec]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r => (
      (r.name || "").toLowerCase().includes(s) ||
      (r.position || "").toLowerCase().includes(s) ||
      (r.department || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s) ||
      (r.phone || "").toLowerCase().includes(s)
    ));
  }, [rows, q]);

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Employee Directory</h3>
        <div className="d-flex align-items-center gap-2 print-hide">
          <HRCampusSelector selectedCampus={campus} onChange={setCampus} allowAll={isExec} />
          <input
            className="form-control"
            placeholder="Search name/position/department"
            style={{ width: 280 }}
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          <button className="btn btn-outline-secondary" onClick={() => window.print()}>Print</button>
          <button className="btn btn-primary" onClick={() => exportCSV(`employee-directory-${campus}.csv`, filtered)}>Export CSV</button>
        </div>
      </div>
      <p className="text-muted">Directory of all employees with contact details and compensation summary.</p>
      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Position</th>
              <th>Department</th>
              <th>Monthly Salary</th>
              <th>Email</th>
              <th>Phone</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-muted">No employees found</td>
              </tr>
            ) : filtered.map((r, idx) => (
              <tr key={idx}>
                <td>{r.name || ""}</td>
                <td>{r.position || ""}</td>
                <td>{r.department || ""}</td>
                <td>₱{Number(r.salary || 0).toLocaleString()}</td>
                <td>{r.email || ""}</td>
                <td>{r.phone || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
